// Layout principal con barra lateral. El menú se filtra por rol del usuario.
// Aquí viven además los componentes compartidos de interfaz (tarjetas, botones,
// avisos). Todo se escribe con las clases del tema — ver docs/INTERFAZ.md.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { ErroresAPI } from "../api/endpoints";
import { useAuthStore, hasRole } from "../stores/auth";
import { useVersion, VERSION_BUNDLE } from "../version";

interface NavItem { to: string; label: string; icono: string; roles?: string[]; }

const NAV: NavItem[] = [
  { to: "/dashboard", label: "Inicio", icono: "🏠" },
  { to: "/dictionary", label: "Diccionario", icono: "📖" },
  { to: "/descriptive", label: "Informe descriptivo", icono: "📊" },
  { to: "/mapa", label: "Mapa territorial", icono: "🗺️" },
  { to: "/exit-poll", label: "Exit poll", icono: "🗳️" },
  { to: "/conteo-rapido", label: "Conteo rápido", icono: "🧮" },
  { to: "/datasets", label: "Acumulados", icono: "📚" },
  { to: "/coding", label: "Codificación", icono: "🏷️" },
  { to: "/open-text", label: "Preguntas abiertas", icono: "💬" },
  { to: "/weighting", label: "Ponderación", icono: "⚖️" },
  { to: "/crosstabs", label: "Crosstabs", icono: "🔀" },
  { to: "/regression", label: "Regresión", icono: "📈" },
  { to: "/timeseries", label: "Series de tiempo", icono: "🕒" },
  { to: "/bootstrap", label: "Bootstrap", icono: "🎲" },
  { to: "/reports", label: "Informes", icono: "📄" },
  { to: "/admin/users", label: "Usuarios", icono: "👥", roles: ["admin"] },
  { to: "/admin/permissions", label: "Permisos", icono: "🔑", roles: ["admin"] },
  { to: "/admin/prompts", label: "Prompts", icono: "✍️", roles: ["admin"] },
  { to: "/admin/configuracion", label: "Configuración", icono: "⚙️", roles: ["admin"] },
  { to: "/admin/errores", label: "Errores", icono: "🔔", roles: ["admin"] },
];

/**
 * Número de versión del sistema, siempre a la vista.
 *
 * 🔴 Sale de la API, así que es la que de verdad corre; si el bundle cargado es
 * otro, lo dice en vez de callarlo. Sin ese aviso, un 200 sirviendo el fichero de
 * ayer es indistinguible de uno correcto, que es justo la pregunta que uno se hace
 * cuando algo no cuadra después de un despliegue.
 *
 * Hereda el color de lo que tenga al lado (`currentColor`): esta etiqueta vive en
 * la cabecera de la barra, en el chip del móvil y en el título del acceso, con
 * fondos distintos. Un color fijo acierta en uno y desaparece en otro.
 */
export function EtiquetaVersion({ className = "" }: { className?: string }) {
  const { version, desfasado } = useVersion();
  return (
    <span
      className={`align-middle font-medium ${desfasado ? "text-estado-critico" : "opacity-60"} ${className}`}
      title={
        desfasado
          ? `El panel cargado es la v${VERSION_BUNDLE} y el servidor corre la v${version}. ` +
            "Recarga con Ctrl+Shift+R para traer la versión nueva."
          : "Versión del sistema"
      }
    >
      v{version}
      {desfasado && " ⚠"}
    </span>
  );
}

export default function Layout() {
  const { me, logout } = useAuthStore();
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const items = NAV.filter((n) => !n.roles || hasRole(me, ...n.roles));

  // Contador de la campana. Se refresca solo cada minuto: un fallo que empieza a
  // repetirse tiene que notarse sin que nadie vaya a mirar.
  const esAdmin = hasRole(me, "admin");
  const pendientes = useQuery({
    queryKey: ["errores-pendientes"],
    queryFn: ErroresAPI.pendientes,
    enabled: esAdmin,
    refetchInterval: 60_000,
    // Un fallo al pedir el contador no puede llenar la pantalla de avisos: lo que
    // importa se ve igualmente al entrar en la pantalla de Errores.
    retry: false,
  }).data?.pendientes ?? 0;

  return (
    // `min-w-0` en el contenedor y en `main`: sin él, un hijo ancho (una tabla, un
    // gráfico) estira el flex en vez de desbordarse dentro de su propio scroll, y
    // toda la página se va de lado.
    <div className="min-h-screen flex min-w-0">
      {/* Botón de menú y marca de versión: fijos arriba a la izquierda, visibles
          esté el menú abierto o cerrado. */}
      <button
        className="md:hidden fixed top-3 left-3 z-40 btn-sec"
        onClick={() => setAbierto(!abierto)}
        aria-expanded={abierto}
        aria-label={abierto ? "Cerrar el menú" : "Abrir el menú"}
      >
        ☰{!abierto && <EtiquetaVersion className="text-[11px] ml-1" />}
      </button>

      {/* Fondo que cierra el menú al tocar fuera. Solo en pantallas estrechas:
          con la barra fija, sin esto no hay forma de cerrarla sin navegar. */}
      {abierto && (
        <div
          className="md:hidden fixed inset-0 z-20 bg-tinta/30"
          onClick={() => setAbierto(false)}
          aria-hidden
        />
      )}

      <aside
        className={`${abierto ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-transform fixed md:static z-30 w-60 shrink-0 h-screen md:h-auto bg-superficie border-r border-grid flex flex-col`}
      >
        {/* Marca de versión: esquina superior izquierda, pegada al nombre y
            siempre a la vista, esté el menú abierto o no. */}
        {/* `pl-12` en móvil: el botón ☰ es fijo y, sin ese hueco, se sienta encima
            del nombre cuando el menú está abierto. */}
        <div className="p-4 pl-12 md:pl-4 border-b border-grid">
          <h1 className="text-lg font-bold text-tinta">
            Analista <EtiquetaVersion className="text-[11px]" />
          </h1>
          <div className="text-xs text-tinta-3 mt-0.5">Visor estadístico y codificación</div>
        </div>

        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setAbierto(false)}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-marca/10 text-marca font-medium"
                    : "text-tinta-2 hover:bg-plano"
                }`
              }
            >
              <span aria-hidden>{n.icono}</span>
              <span className="flex-1">{n.label}</span>
              {n.to === "/admin/errores" && pendientes > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-estado-critico text-white"
                  title={`${pendientes} error(es) sin revisar`}
                >
                  {pendientes > 99 ? "99+" : pendientes}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-grid text-sm">
          <div className="truncate text-tinta-2">{me?.email}</div>
          <div className="mt-1 inline-block px-2 py-0.5 rounded bg-plano border border-grid text-xs text-tinta-3 capitalize">
            {me?.role}
          </div>
          <button
            className="btn-sec mt-2 w-full"
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* `pt-14` en móvil deja sitio al botón flotante, que si no tapa el título
          de cada página. */}
      <main className="flex-1 min-w-0 overflow-auto pt-14 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-tinta">{title}</h1>
      {subtitle && <p className="text-sm text-tinta-3 mt-1">{subtitle}</p>}
    </div>
  );
}

export function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`tarjeta ${className}`}>{children}</div>;
}

export function Spinner() {
  return (
    <div className="inline-block w-4 h-4 border-2 border-marca border-t-transparent rounded-full animate-spin" />
  );
}

export function useProjectFormPicker() {
  // Hook reutilizable: carga proyectos/formularios. Lo usan varias páginas.
  // (Implementado en cada página con TanStack Query para mantener simplicidad.)
  return null;
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="p-3 rounded-md bg-estado-critico/10 border border-estado-critico/40 text-estado-critico text-sm">
      {message}
    </div>
  );
}

// Aviso informativo (no es un error): algo salió sin efecto, o hay una salvedad
// que el operador debe leer. El silencio no es información.
export function InfoBanner({ message }: { message: string }) {
  return (
    <div className="p-3 rounded-md bg-estado-warning/10 border border-estado-warning/40 text-tinta-2 text-sm">
      {message}
    </div>
  );
}

export function OkBanner({ message }: { message: string }) {
  return (
    <div className="p-3 rounded-md bg-estado-ok/10 border border-estado-ok/40 text-tinta-2 text-sm">
      {message}
    </div>
  );
}

// Panel de documentación plegable. Úsalo en cada página de análisis para
// explicar en pantalla qué hace cada opción.
export function HelpBox({ title = "¿Cómo funciona esta página?", children, defaultOpen = false }: {
  title?: string; children: React.ReactNode; defaultOpen?: boolean;
}) {
  return (
    <details open={defaultOpen} className="mb-4 bg-marca/5 border border-marca/20 rounded-lg overflow-hidden">
      <summary className="cursor-pointer select-none px-4 py-2.5 text-sm font-medium text-marca hover:bg-marca/10">
        📘 {title}
      </summary>
      <div className="px-4 pb-4 pt-1 text-sm text-tinta-2 space-y-2 leading-relaxed">{children}</div>
    </details>
  );
}

/**
 * «Todas / Ninguna» y el recuento, para cualquier lista de selección múltiple.
 *
 * Vive aquí y no en cada página para que se comporte igual en todas: hasta ahora
 * lo tenían Regresión e Informes, cada uno escrito a mano y con su propio criterio.
 *
 * `visibles` son los elementos que se pueden marcar **ahora mismo**: si hay un
 * buscador activo, «Todas» marca lo que se está viendo, no lo que hay detrás del
 * filtro. Marcar a ciegas cosas que no están en pantalla es la forma rápida de
 * lanzar un análisis sobre variables que nadie eligió.
 */
export function AccionesSeleccion({
  visibles,
  seleccionados,
  onCambiar,
  nombre = "seleccionada(s)",
  filtrando = false,
}: {
  visibles: string[];
  seleccionados: string[];
  onCambiar: (nombres: string[]) => void;
  nombre?: string;
  filtrando?: boolean;
}) {
  const enPantalla = new Set(visibles);
  const yaTodas = visibles.length > 0 && visibles.every((v) => seleccionados.includes(v));

  const marcarTodas = () =>
    // Se conserva lo ya marcado que el filtro esconde: si no, buscar una variable
    // y pulsar «Todas» borraría en silencio la selección anterior.
    onCambiar(Array.from(new Set([...seleccionados, ...visibles])));

  const quitarTodas = () =>
    onCambiar(seleccionados.filter((s) => !enPantalla.has(s)));

  const boton =
    "text-xs px-2 py-0.5 rounded border border-grid bg-plano hover:bg-grid " +
    "disabled:opacity-40 disabled:cursor-not-allowed text-tinta-2";

  // Con una sola opción, los botones no ahorran nada: la casilla está ahí al lado.
  // El recuento sí se mantiene, para que la línea no baile al filtrar.
  const merecenLaPena = visibles.length > 1;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-tinta-3">
        {seleccionados.length} de {visibles.length} {nombre}
        {filtrando && " (filtrado)"}
      </span>
      {merecenLaPena && (
        <>
      <button
        type="button"
        onClick={marcarTodas}
        disabled={yaTodas || visibles.length === 0}
        className={boton}
        title={filtrando ? "Marca las que se ven ahora, sin perder las ya marcadas" : "Marcar todas"}
      >
        Todas
      </button>
      <button
        type="button"
        onClick={quitarTodas}
        disabled={seleccionados.length === 0}
        className={boton}
        title={filtrando ? "Desmarca solo las que se ven ahora" : "Desmarcar todas"}
      >
        Ninguna
      </button>
        </>
      )}
    </div>
  );
}

// Etiqueta pequeña con el tipo de una variable.
export function KindBadge({ kind }: { kind: "numeric" | "categorical" | "other" }) {
  const cfg = {
    numeric: { txt: "numérica", cls: "bg-estado-ok/10 text-estado-ok border-estado-ok/30" },
    categorical: { txt: "categórica", cls: "bg-estado-warning/15 text-estado-serio border-estado-warning/40" },
    other: { txt: "texto", cls: "bg-plano text-tinta-3 border-grid" },
  }[kind];
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cfg.cls}`}>{cfg.txt}</span>;
}
